import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Divider,
  Skeleton,
  Stack,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import QuizIcon from '@mui/icons-material/Quiz';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const AssessmentList = ({ ...rest }) => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    fetch(`${process.env.REACT_APP_API_URL}/api/gettraineeassessmentdetail/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('token'),
      },
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (!isMounted) return;
        if (!data.detail && Array.isArray(data) && data.length > 0) {
          const normalizedData = data.map((item) => ({
            labId: item.lab_id,
            labName: item.lab_name,
            assessmentId: item.assessment_id,
            assessmentName: item.assessment_name,
            assessmentDate: item.assessment_date,
            labelName: item.labelName,
            traineeId: item.trainee_id,
            timer: item.timer,
            totalAssessmentNo: item.total_assessment_no,
            formattedDate: item.assessment_date
              ? new Date(item.assessment_date).toLocaleDateString()
              : 'NA',
          }));
          setAssessments(normalizedData);
        } else {
          setAssessments([]);
        }
      })
      .catch(() => {
        if (isMounted) {
          setAssessments([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleRedirect = useCallback(
    (assessment) => {
      navigate('/app/assessmentquestion', {
        state: {
          instanceLabName: assessment.labName,
          assessmentId: assessment.assessmentId,
          assessmentLinkName: assessment.assessmentName,
          instanceLabId: assessment.labId,
          traineeId: assessment.traineeId,
          stimer: assessment.timer,
          temptimeout: 1,
        },
      });
    },
    [navigate]
  );

  const filteredAssessments = useMemo(() => {
    if (!searchTerm.trim()) return assessments;
    const searchLower = searchTerm.toLowerCase();
    return assessments.filter(
      (item) =>
        item.labName?.toLowerCase().includes(searchLower) ||
        item.assessmentName?.toLowerCase().includes(searchLower) ||
        item.labelName?.toLowerCase().includes(searchLower)
    );
  }, [assessments, searchTerm]);

  const paginatedAssessments = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAssessments.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAssessments, page, rowsPerPage]);

  const stats = useMemo(() => {
    return {
      total: assessments.length,
      available: filteredAssessments.length,
    };
  }, [assessments.length, filteredAssessments.length]);

  const traineeName = localStorage.getItem('user') || 'NA';
  const labName = assessments.length > 0 ? assessments[0].labName : 'NA';

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderSkeleton = useMemo(
    () => (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={index}>
            <Card variant="outlined">
              <CardContent>
                <Skeleton variant="text" width="60%" height={28} />
                <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: 1 }} />
                </Stack>
                <Skeleton variant="text" width="50%" height={20} sx={{ mt: 1.5 }} />
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: 1 }} />
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    ),
    []
  );

  const renderEmptyState = useMemo(
    () => (
      <Box
        sx={{
          mt: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <QuizIcon sx={{ fontSize: 64, mb: 1, color: 'text.secondary' }} />
        <Typography variant="h6">No assessments available</Typography>
        <Typography variant="body2" align="center" sx={{ maxWidth: 360, mt: 1 }}>
          {searchTerm
            ? 'No assessments match your search. Try adjusting your search terms.'
            : "We'll notify you when new assessments are assigned to you."}
        </Typography>
      </Box>
    ),
    [searchTerm]
  );

  const renderCards = useMemo(
    () => (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {paginatedAssessments.map((assessment, index) => {
          const actualIndex = page * rowsPerPage + index;
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={`${assessment.assessmentId}-${actualIndex}`}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: 'primary.light',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Stack spacing={1.25}>
                    <Typography variant="h6" noWrap>
                      {assessment.assessmentName}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        label={assessment.labName || 'Lab'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      {assessment.timer && (
                        <Chip
                          icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                          label={`${assessment.timer} min`}
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      )}
                    </Stack>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        Lab: {assessment.labName}
                      </Typography>
                      {assessment.formattedDate && assessment.formattedDate !== 'NA' && (
                        <Typography variant="body2" color="text.secondary">
                          Date: {assessment.formattedDate}
                        </Typography>
                      )}
                      {assessment.labelName && (
                        <Typography variant="body2" color="text.secondary">
                          Label: {assessment.labelName}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
                <CardActions sx={{ px: 2.5, pb: 2.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() => handleRedirect(assessment)}
                    startIcon={<QuizIcon />}
                  >
                    Start Assessment
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    ),
    [paginatedAssessments, page, rowsPerPage, handleRedirect]
  );

  return (
    <Container maxWidth={false} sx={{ pt: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
      <Card {...rest} sx={{ mb: 4 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            p: { xs: 2.5, md: 3 },
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} alignItems="center">
                <SchoolIcon color="primary" />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Lab name
                  </Typography>
                  <Typography variant="h5">{labName}</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                direction="row"
                spacing={2}
                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonIcon color="primary" />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Trainee
                    </Typography>
                    <Typography variant="h6">{traineeName}</Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Grid container spacing={2} sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={1} alignItems="flex-start">
                  <AssignmentIcon color="primary" fontSize="large" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Total assessments
                  </Typography>
                  <Typography variant="h4">{stats.total}</Typography>
                  <Chip label={`${stats.available} available`} color="primary" size="small" />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={8}>
            <Stack spacing={2}>
              <Typography variant="subtitle2">Search assessments</Typography>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search by Lab Name or Assessment Name"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: '100%', maxWidth: { xs: '100%', sm: 400, md: 500 } }}
              />
            </Stack>
          </Grid>
        </Grid>

        <Divider />

        <Box sx={{ px: { xs: 2.5, md: 3 }, py: 3 }}>
          {loading ? (
            renderSkeleton
          ) : filteredAssessments.length ? (
            <>
              {renderCards}
              {filteredAssessments.length > 0 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
                  <TablePagination
                    rowsPerPageOptions={[6, 9, 12, 15]}
                    component="div"
                    count={filteredAssessments.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                      '& .MuiTablePagination-toolbar': {
                        justifyContent: 'flex-start',
                        paddingLeft: 2,
                      },
                      '& .MuiTablePagination-spacer': {
                        display: 'none',
                      },
                      '& .MuiTablePagination-selectLabel': {
                        margin: 0,
                      },
                      '& .MuiTablePagination-displayedRows': {
                        margin: 0,
                      },
                    }}
                  />
                </Box>
              )}
            </>
          ) : (
            renderEmptyState
          )}
        </Box>
      </Card>
    </Container>
  );
};

export default AssessmentList;
